/**
 * Education360 role -> navigation groups.
 * Each portal role sees ONLY the modules relevant to them; tenant isolation
 * is enforced additionally on the backend.
 */
import {
  LayoutDashboard, GraduationCap, Users, BookOpen, CalendarDays,
  ClipboardCheck, FileBarChart2, Wallet, School, BedDouble, Bus,
  HeartPulse, Shield, Library, Trophy, UserCog, Bell, FileText,
  Sparkles, ClipboardList, UsersRound, Award, Layers, Palette,
} from "lucide-react";

// Modules keyed by unique id
const M = {
  executive: { id: "executive", label: "Executive Overview", icon: LayoutDashboard, path: "" },
  intelligence: { id: "intelligence", label: "Executive Intelligence", icon: Sparkles, path: "intelligence" },
  studentlife: { id: "studentlife", label: "Student Life360", icon: HeartPulse, path: "studentlife" },
  ai: { id: "ai", label: "AI Executive Intelligence", icon: Sparkles, path: "ai" },
  students: { id: "students", label: "Students", icon: GraduationCap, path: "students" },
  parents: { id: "parents", label: "Parents", icon: UsersRound, path: "parents" },
  academics: { id: "academics", label: "Academics", icon: BookOpen, path: "academics" },
  attendance: { id: "attendance", label: "Attendance", icon: ClipboardCheck, path: "attendance" },
  timetable: { id: "timetable", label: "Timetable", icon: CalendarDays, path: "timetable" },
  assessments: { id: "assessments", label: "Assessments", icon: ClipboardList, path: "assessments" },
  reports: { id: "reports", label: "Report Cards", icon: FileBarChart2, path: "reports" },
  finance: { id: "finance", label: "Finance", icon: Wallet, path: "finance" },
  admissions: { id: "admissions", label: "Admissions", icon: School, path: "admissions" },
  boarding: { id: "boarding", label: "Boarding", icon: BedDouble, path: "boarding" },
  transport: { id: "transport", label: "Transport", icon: Bus, path: "transport" },
  medical: { id: "medical", label: "Medical", icon: HeartPulse, path: "medical" },
  behaviour: { id: "behaviour", label: "Behaviour", icon: Shield, path: "behaviour" },
  library: { id: "library", label: "Library", icon: Library, path: "library" },
  sports: { id: "sports", label: "Sports & Clubs", icon: Trophy, path: "sports" },
  hr: { id: "hr", label: "Human Resources", icon: UserCog, path: "hr" },
  staff: { id: "staff", label: "Staff Directory", icon: Users, path: "staff" },
  notifications: { id: "notifications", label: "Notifications", icon: Bell, path: "notifications" },
  documents: { id: "documents", label: "Documents", icon: FileText, path: "documents" },
  schoolAdmin: { id: "schoolAdmin", label: "School Administration", icon: UserCog, path: "school-admin" },
  publishing: { id: "publishing", label: "Publishing Centre", icon: FileText, path: "publishing" },
  boardPack: { id: "boardPack", label: "Executive Board Pack", icon: FileBarChart2, path: "board-pack" },
  archive: { id: "archive", label: "Report Archive", icon: FileText, path: "archive" },
  credentials: { id: "credentials", label: "Credentials Centre", icon: Trophy, path: "credentials" },
  distribution: { id: "distribution", label: "Distribution Outbox", icon: Bell, path: "distribution" },
  progressIntel: { id: "progressIntel", label: "Progress Intelligence", icon: Sparkles, path: "progress" },
  designer: { id: "designer", label: "Document Designer", icon: UserCog, path: "designer" },
  parentVault: { id: "parentVault", label: "Family Vault", icon: FileText, path: "vault" },
  parentPortal: { id: "parent", label: "My Family", icon: UsersRound, path: "parent" },
  parentFinance: { id: "parentFinance", label: "Family Finance", icon: Wallet, path: "parent/finance" },
  passport: { id: "passport", label: "Digital Passport", icon: Award, path: "passport" },
  studentPortal: { id: "student", label: "My School", icon: GraduationCap, path: "student" },
  staffPortal: { id: "staffPortal", label: "My Classes", icon: BookOpen, path: "staff" },
  academic: { id: "academic", label: "Academic360", icon: Award, path: "academic" },
  designStudio: { id: "designStudio", label: "Design Studio", icon: Palette, path: "design-studio" },
  blueprintLibrary: { id: "blueprintLibrary", label: "Blueprint Library", icon: Layers, path: "design-studio/blueprints" },
  health: { id: "health", label: "Health360", icon: HeartPulse, path: "health" },
  academicWorkbench: { id: "academicWorkbench", label: "Teacher Workbench", icon: BookOpen, path: "academic/workbench" },
  markbooks: { id: "markbooks", label: "Mark Books", icon: ClipboardList, path: "academic/markbooks" },
  gradebook: { id: "gradebook", label: "Grade Books", icon: Award, path: "academic/gradebook" },
  homework: { id: "homework", label: "Homework360", icon: BookOpen, path: "academic/homework" },
  moderation: { id: "moderation", label: "Moderation360", icon: ClipboardCheck, path: "academic/moderation" },
  promotion: { id: "promotion", label: "Promotion360", icon: GraduationCap, path: "academic/promotion" },
  curriculumStudio: { id: "curriculumStudio", label: "Curriculum Studio", icon: Layers, path: "academic/curriculum" },
};

const EXECUTIVE_NAV = [
  M.executive, M.intelligence, M.ai, M.students, M.academics, M.attendance, M.assessments,
  M.academic, M.markbooks, M.gradebook, M.homework, M.moderation, M.promotion, M.curriculumStudio,
  M.studentlife, M.health,
  M.reports, M.publishing, M.boardPack, M.archive,
  M.finance, M.admissions, M.boarding, M.transport, M.medical,
  M.behaviour, M.library, M.sports, M.hr, M.staff, M.notifications, M.documents,
  M.designStudio, M.blueprintLibrary, M.schoolAdmin,
];

export const NAV_BY_ROLE = {
  executive: EXECUTIVE_NAV,
  principal: EXECUTIVE_NAV,
  deputy_head: EXECUTIVE_NAV,
  board: [M.executive, M.intelligence, M.ai, M.academic, M.studentlife, M.health, M.reports, M.publishing, M.boardPack, M.archive, M.finance, M.admissions, M.notifications, M.documents],
  administrator: EXECUTIVE_NAV,
  teacher: [M.staffPortal, M.academicWorkbench, M.markbooks, M.homework, M.moderation, M.students, M.attendance, M.assessments, M.timetable, M.studentlife, M.library, M.notifications],
  parent: [M.parentPortal, M.passport, M.parentFinance, M.parentVault, M.reports, M.progressIntel, M.studentlife, M.archive, M.notifications, M.documents],
  student: [M.studentPortal, M.passport, M.timetable, M.library, M.sports, M.studentlife, M.notifications],
  finance: [M.finance, M.students, M.notifications, M.documents],
  admissions: [M.admissions, M.students, M.notifications, M.documents],
  boarding: [M.boarding, M.studentlife, M.health, M.students, M.notifications, M.medical],
  health: [M.health, M.medical, M.studentlife, M.students, M.notifications],
  nurse: [M.health, M.medical, M.studentlife, M.students, M.notifications],
  transport: [M.transport, M.students, M.notifications],
  librarian: [M.library, M.students, M.staff, M.notifications],
  hr: [M.hr, M.staff, M.notifications, M.documents],
};

export const ROLE_LABEL = {
  executive: "Executive",
  principal: "Principal",
  deputy_head: "Deputy Head",
  board: "Board Member",
  administrator: "School Administrator",
  teacher: "Teacher",
  parent: "Parent",
  student: "Student",
  finance: "Bursar / Finance",
  admissions: "Admissions Registrar",
  boarding: "Housemaster",
  health: "School Nurse",
  transport: "Transport Manager",
  librarian: "Librarian",
  hr: "Human Resources",
};
